import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash.get';

import getPlaymakerMetadata from 'components/packages/Playmaker/utils/getMetadata';
import { content as ContentPropType } from 'lib/CustomPropTypes';

import LiveBlog from '../../LiveBlog';
import FeaturedTease from '../FeaturedTease';
import FeaturedVideo from '../FeaturedVideo';
import Tease from '../Tease';
import TeaseLayout from '../TeaseLayouts';
import Title from '../Title';

const FEATURED_ITEM_INDEX = 0;
const LIVE_BLOG_INDEX = 1;

class LiveBlogCoverSpread extends React.Component {
  static propTypes = {
    content: ContentPropType.isRequired,
  };

  static childContextTypes = {
    setStickyVideoFlag: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      hasClickedToPlay: false,
      hasStickyVideo: false,
    };
  }

  shapeItem = (item = {}, packageMetadata = {}) => ({
    ...item,
    metadata: { // merge content and package metadata
      ...packageMetadata,
      ...(item || {}).metadata,
    },
  });

  getLiveBlog = (items, packageMetadata) => {
    const itemAtIndex = get(items, `[${LIVE_BLOG_INDEX}]`) || {};
    if (itemAtIndex.type !== 'liveBlog') return null;
    return this.shapeItem(itemAtIndex, packageMetadata);
  };

  getFeaturedItem = (items, packageMetadata) => {
    const itemAtIndex = get(items, `[${FEATURED_ITEM_INDEX}]`);
    return this.shapeItem(itemAtIndex, packageMetadata);
  };

  setStickyVideoFlag = (hasStickyVideo) => {
    this.setState({ hasStickyVideo });
  };

  getChildContext = () => ({
    setStickyVideoFlag: this.setStickyVideoFlag,
  });

  onPlayClick = () => {
    this.setState({ hasClickedToPlay: true });
  };

  render() {
    const { content = {} } = this.props; // the package
    const {
      id: packageId,
      items = [],
      metadata: packageMetadata,
    } = content;
    const { hasStickyVideo, hasClickedToPlay } = this.state;
    if (!items || !items.length) return null;

    const {
      showBreakingHeader: breakingStatus,
      showEyebrow: showUnibrowStatus,
      title,
      titleUrl,
    } = packageMetadata || {};

    const {
      playmakerToggle,
      playmakerId,
    } = getPlaymakerMetadata(packageMetadata);
    const isPlaymaker = playmakerToggle === true;

    const featuredItem = this.getFeaturedItem(items, packageMetadata);
    const liveBlog = this.getLiveBlog(items, packageMetadata);
    const isBreaking = breakingStatus !== false;
    const showUnibrow = showUnibrowStatus !== false;

    // filtering in order to remove any articles that are returned as null
    const teases = items.filter((item, index) => { if (index > 1) return item; return null; });

    const hasFeaturedItem = !!featuredItem.id; // package metadata is shaped onto item, so check id
    const isFeaturedVideo = featuredItem.type === 'video' || isPlaymaker;
    const isFeaturedTease = !isFeaturedVideo;

    const isLiveBlogBreaking = get(liveBlog, 'metadata.showBreakingStyle', true); // true by default

    return (
      <div
        className={classNames('pkg cover-spread', { 'cover-spread--breaking': isBreaking, 'cover-spread--stuck': hasStickyVideo })}
        data-test="cover-spread"
        data-packageid={packageId}
      >
        <div className="layout-grid-container">
          <div className="layout-grid-item grid-col-12">
            <div className="pkg">
              <div className="cover-spread__headline__wrapper">
                <Title title={title} titleUrl={titleUrl} />
              </div>

              <div className="liveblog-cover-spread__wrapper">
                <button
                  className="liveblog-cover-spread__feature cover-spread__column"
                  data-test="liveblog-cover-spread__feature"
                  onClick={this.onPlayClick}
                  type="button"
                >
                  { hasFeaturedItem && isFeaturedVideo && (
                    // making this a "fully uncontrolled component with key"
                    // https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
                    <FeaturedVideo
                      key={`${featuredItem.id}${isPlaymaker ? `-withPlaymakerId${playmakerId}` : ''}`}
                      content={featuredItem}
                      hasClickedToPlay={hasClickedToPlay}
                    />
                  )}
                  { hasFeaturedItem && isFeaturedTease && (
                    <FeaturedTease
                      content={featuredItem}
                      showUnibrow={showUnibrow}
                    />
                  )}
                </button>

                <div
                  className={classNames('liveblog-cover-spread__live-blog', 'liveblog-cover-spread__column', {
                    'liveblog-cover-spread__live-blog--breaking': isLiveBlogBreaking,
                  })}
                  data-test="liveblog-cover-spread__live-blog"
                >
                  { liveBlog && (
                    <LiveBlog
                      // must pass package object as 'content',
                      // so subcomponent knows it's not the root package
                      content={{
                        ...content,
                        items: [{
                          ...liveBlog,
                          metadata: {
                            ...liveBlog.metadata,
                            showDek: false, // dek not supported, override
                          },
                        }],
                      }}
                    />
                  )}
                </div>

                <div className="liveblog-cover-spread__teases liveblog-cover-spread__column" data-test="liveblog-cover-spread__teases">
                  <TeaseLayout
                    layoutType="liveBlog"
                    totalTeases={teases.length}
                    sectionTeaseCount={teases.length}
                    render={(layout) => (
                      teases.map((tease, index) => (
                        <Tease
                          key={`cs-tease-${tease.id}`}
                          content={tease}
                          showInlineIcon
                          showUnibrow={showUnibrow}
                          layout={layout}
                          index={index}
                        />
                      ))
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default LiveBlogCoverSpread;
